import { BracketsCurly } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { cn } from 'src/utils/className';
import { Button } from './Button';
import { Textarea, TextareaProps } from './Textarea';
import { VariablesDropdownMenu } from './VariablesDropdownMenu';
import getCaretCoordinates from 'textarea-caret';

interface PromptTextAreaProps extends Omit<TextareaProps, 'value'> {
  value: string;
  loading?: boolean;
  variables?: string[];
  autoScroll?: boolean;
}

const CARET_OFFSET_X = 4;
const CARET_OFFSET_Y = 40;

export const PromptTextArea = ({
  value,
  onChange,
  loading = false,
  variables = [],
  placeholder = 'Prompt Content',
  rows = 11,
  autoScroll = false,
  className,
  onBlur,
}: PromptTextAreaProps) => {
  const [isVariableDropdownOpen, setIsVariableDropdownOpen] = useState(false);
  const [showCurlyBraceTooltip, setShowCurlyBraceTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  const [footerEngaged, setFooterEngaged] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownContainerRef = useRef<HTMLDivElement>(null);

  const showFooter = rows > 1 || isTextareaFocused || footerEngaged;

  useEffect(() => {
    if (autoScroll && textareaRef.current) {
      textareaRef.current.scrollTo({
        top: textareaRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [autoScroll, value]);

  const insertAtCursor = (newText: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const currentContent = value;

    textarea.focus();

    // Use deprecated API in order to preserve undo functionality in text area
    // If it's not supported by browser fallback to setting state
    if (!document.execCommand('insertText', false, newText)) {
      const currentContentStr =
        typeof currentContent === 'string' ? currentContent : '';

      currentContentStr.substring(selectionEnd);
    }

    // Move the cursor to the end of the inserted text
    textarea.selectionStart = selectionStart + newText.length;
    textarea.selectionEnd = selectionStart + newText.length;

    //Refocus on the textarea after updating
    textarea.focus();
  };

  const handleVariableSelect = (variable: string, removeFirstBrace = false) => {
    insertAtCursor(removeFirstBrace ? variable.slice(1) : variable);
    setIsVariableDropdownOpen(false);
  };

  // Update the change handler to use caret coordinates
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const prevContent = value;
    const newContent = e.target.value;
    onChange?.(e);

    const cursorPosition = e.target.selectionStart;

    // Detect insertion of '{' (not deletion)
    if (
      newContent.length > prevContent.length && // Only on insert, not delete
      newContent[cursorPosition - 1] === '{' &&
      (prevContent[cursorPosition - 2] !== '{' || prevContent.length === 0)
    ) {
      const caret = getCaretCoordinates(e.target, e.target.selectionEnd);
      const textareaRect = textareaRef.current?.getBoundingClientRect();

      if (textareaRect) {
        setTooltipPosition({
          x: textareaRect.left + caret.left + CARET_OFFSET_X,
          y: textareaRect.top + caret.top + CARET_OFFSET_Y,
        });
        setShowCurlyBraceTooltip(true);
      }
    } else if (
      newContent.length > prevContent.length &&
      newContent[cursorPosition - 1] === '}'
    ) {
      setShowCurlyBraceTooltip(false);
    } else if (
      newContent.length < prevContent.length && // On delete
      // If the deleted character was '{', don't show the tooltip
      prevContent[cursorPosition] === '{'
    ) {
      setShowCurlyBraceTooltip(false);
    }
  };

  // Update the keydown handler to also use caret coordinates
  const handleTextareaKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (e.key === 'Escape') {
      setShowCurlyBraceTooltip(false);
      return;
    }

    // Also check for { on keydown to be more responsive
    const textarea = e.target as HTMLTextAreaElement;
    const cursorPosition = textarea.selectionStart;
    const charAtCursor = textarea.value[cursorPosition - 1];

    if (charAtCursor === '{') {
      const caret = getCaretCoordinates(textarea, textarea.selectionEnd);
      const textareaRect = textarea.getBoundingClientRect();

      setTooltipPosition({
        x: textareaRect.left + caret.left + CARET_OFFSET_X,
        y: textareaRect.top + caret.top + CARET_OFFSET_Y,
      });
      setShowCurlyBraceTooltip(true);
    }
  };

  const handleTextareaBlur = async (
    e: React.FocusEvent<HTMLTextAreaElement>,
  ) => {
    await new Promise((resolve) => setTimeout(resolve, 200));
    setShowCurlyBraceTooltip(false);
    setIsTextareaFocused(false);
    onBlur?.(e);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      const clickedInsideContainer =
        !!dropdownContainerRef.current?.contains(targetNode);
      if (!clickedInsideContainer) {
        setIsVariableDropdownOpen(false);
        setFooterEngaged(false);
      }
    };

    if (isVariableDropdownOpen || footerEngaged) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVariableDropdownOpen, footerEngaged]);

  const countLines = (content: string) => {
    if (!textareaRef.current) return 1;
    const textarea = textareaRef.current;
    const style = window.getComputedStyle(textarea);
    const textareaWidth = textarea.clientWidth;
    const font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;

    // Helper to measure text width
    const getTextWidth = (text: string, font: string) => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return 0;
      context.font = font;
      return context.measureText(text).width;
    };

    // Split by lines, but also account for wrapping
    const lines = content.split('\n');
    let totalLines = 0;
    for (const line of lines) {
      if (line === '') {
        totalLines += 1;
        continue;
      }
      const textWidth = getTextWidth(line, font);
      // Add at least 1 line, or more if it wraps
      totalLines += Math.max(1, Math.ceil(textWidth / textareaWidth));
    }
    return totalLines;
  };

  // Calculate possible lines by current textarea height and line height
  const possibleLines = () => {
    if (!textareaRef.current) return 0;
    const textarea = textareaRef.current;
    const style = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(style.lineHeight);
    if (!lineHeight) return 0;
    return Math.floor(textarea.clientHeight / lineHeight);
  };

  const buttonShouldBeMoved = countLines(value as string) + 2 > possibleLines();

  return (
    <div className="relative">
      <Textarea
        placeholder={placeholder}
        value={value}
        onChange={handleTextareaChange}
        onKeyDown={handleTextareaKeyDown}
        onFocus={() => setIsTextareaFocused(true)}
        onBlur={handleTextareaBlur}
        disabled={loading}
        rows={rows}
        ref={textareaRef}
        footer={
          showFooter ? (
            <div
              ref={dropdownContainerRef}
              className={cn(
                'relative',
                buttonShouldBeMoved && 'absolute mt-4 -ml-2 w-40',
              )}
              onFocusCapture={() => setFooterEngaged(true)}
            >
              <Button
                ref={buttonRef}
                variant={buttonShouldBeMoved ? 'contained' : 'text'}
                size="small"
                className={cn(
                  'relative text-neutral-400 hover:text-primary-600 flex items-center gap-2 justify-center backdrop-blur-sm z-50',
                  buttonShouldBeMoved && 'text-neutral-0 hover:text-neutral-0',
                )}
                onClick={() => {
                  setFooterEngaged(true);
                  setIsVariableDropdownOpen(!isVariableDropdownOpen);
                }}
              >
                <BracketsCurly size={20} /> Add variable
              </Button>

              <VariablesDropdownMenu
                isOpen={isVariableDropdownOpen}
                setIsOpen={setIsVariableDropdownOpen}
                promptVariables={variables}
                handleVariableSelect={handleVariableSelect}
                position="top-left"
              />
            </div>
          ) : undefined
        }
        className={className}
      />
      {showCurlyBraceTooltip && (
        <div
          className="fixed z-50"
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y - 25, // Position above the cursor
          }}
        >
          <VariablesDropdownMenu
            isOpen={showCurlyBraceTooltip}
            setIsOpen={setShowCurlyBraceTooltip}
            promptVariables={variables}
            handleVariableSelect={(variable) =>
              handleVariableSelect(variable, true)
            }
          />
        </div>
      )}
    </div>
  );
};
